import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Autocomplete, Box, Checkbox, Chip, CircularProgress, Divider,
    FormControl, FormHelperText, FormLabel, Input, Option, Select, Sheet,
    Stack, Typography,
} from '@mui/joy';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';

import { useAdvertiser } from '../../AdvertiserContext';
import { APIAdsReach } from '../../api_ads';
import { useAdsDictionaries } from './useAdsData';
import { formatInt } from './adsConstants';


const EMPTY = {
    geo_mode: 'all', cities: [], regions: [], exclude_cities: [],
    genders: [], age_min: null, age_max: null, incomes: [], purposes: [],
    hobby_ids: [], field_ids: [], platforms: [],
    only_paid: null, has_photo: null,
    account_age_days_min: null, account_age_days_max: null, traits: [],
};


function MultiChips({ label, options, value, onChange, disabled, helper }) {
    return (
        <FormControl>
            <FormLabel>{label}</FormLabel>
            <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                {options.map((option) => {
                    const active = value.includes(option.value);
                    return (
                        <Chip
                            key={option.value}
                            variant={active ? 'solid' : 'outlined'}
                            color={active ? 'primary' : 'neutral'}
                            disabled={disabled}
                            onClick={() => onChange(
                                active
                                    ? value.filter((v) => v !== option.value)
                                    : [...value, option.value]
                            )}
                            sx={{ cursor: disabled ? 'default' : 'pointer' }}
                        >
                            {option.label}
                        </Chip>
                    );
                })}
            </Stack>
            {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
    );
}


/**
 * Редактор таргетинга группы.
 *
 * Пустое условие означает «показывать всем» — так же трактует сервер.
 * Поэтому не выбрать ни одного пола это не ошибка, а «любой пол»,
 * и подпись под каждым блоком должна об этом говорить прямо: партнёры
 * регулярно понимают пустой список как «никому».
 */
export default function AdTargetingEditor({ value, onChange, disabled, showReach = true }) {
    const { advertiserToken: token } = useAdvertiser();
    const { dictionaries, loading } = useAdsDictionaries();
    const t = { ...EMPTY, ...(value || {}) };

    const [reach, setReach] = useState(null);
    const [reachLoading, setReachLoading] = useState(false);
    const debounceRef = useRef(null);

    const set = (patch) => onChange({ ...t, ...patch });

    // Оценка охвата с задержкой — иначе запрос уходит на каждый клик по чипу
    const loadReach = useCallback(async (targeting) => {
        if (!token) return;
        try {
            setReachLoading(true);
            const res = await APIAdsReach(token, targeting);
            setReach(res?.reach ?? null);
        } catch (e) {
            setReach(null);
        } finally {
            setReachLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => loadReach(t), 600);
        return () => clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(t)]);

    const genderOptions = useMemo(
        () => (dictionaries?.genders || []).map((g) => ({ value: g.value, label: g.label })),
        [dictionaries],
    );
    const incomeOptions = useMemo(
        () => (dictionaries?.incomes || []).map((i) => ({ value: i.value, label: i.label })),
        [dictionaries],
    );
    const purposeOptions = useMemo(
        () => (dictionaries?.purposes || []).map((p) => ({ value: p.value, label: p.label })),
        [dictionaries],
    );
    const platformOptions = useMemo(
        () => (dictionaries?.platforms || []).map((p) => ({ value: p.value, label: p.label })),
        [dictionaries],
    );

    const hobbies = dictionaries?.hobbies || [];
    const fields = dictionaries?.fields_of_activity || [];

    if (loading) {
        return (
            <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress size="sm" />
            </Stack>
        );
    }

    return (
        <Stack spacing={2}>
            {/* Оценка охвата — самый важный элемент экрана.
                В прогнозаторе скрывается: там охват уже показан
                в блоке результата, дублировать его незачем */}
            {showReach && (
            <Sheet
                variant="soft"
                color={reach !== null && reach < 500 ? 'warning' : 'primary'}
                sx={{ p: 1.5, borderRadius: 'md' }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <GroupsRoundedIcon />
                    <Box>
                        <Typography level="body-xs">Подходит под таргетинг</Typography>
                        <Typography level="title-md" sx={{ fontWeight: 700 }}>
                            {reachLoading
                                ? 'считаем…'
                                : reach === null ? '—' : `${formatInt(reach)} чел.`}
                        </Typography>
                    </Box>
                    {reach !== null && reach < 500 && !reachLoading && (
                        <Typography level="body-xs" sx={{ ml: 1 }}>
                            Слишком узко: показов будет мало. Ослабьте условия.
                        </Typography>
                    )}
                </Stack>
            </Sheet>
            )}

            {/* --- ГЕО --- */}
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                <Typography level="title-sm" sx={{ mb: 1.5 }}>География</Typography>

                <FormControl sx={{ mb: 1.5 }}>
                    <FormLabel>Охват</FormLabel>
                    <Select
                        value={t.geo_mode}
                        onChange={(_, v) => v && set({ geo_mode: v })}
                        disabled={disabled}
                    >
                        <Option value="all">Без ограничений</Option>
                        <Option value="cities">Выбранные города</Option>
                        <Option value="regions">Выбранные регионы</Option>
                    </Select>
                </FormControl>

                {t.geo_mode === 'cities' && (
                    <FormControl sx={{ mb: 1.5 }}>
                        <FormLabel>Города</FormLabel>
                        <Autocomplete
                            multiple
                            options={dictionaries?.cities || []}
                            value={t.cities}
                            onChange={(_, v) => set({ cities: v })}
                            disabled={disabled}
                            placeholder="Начните вводить название"
                            limitTags={8}
                        />
                        <FormHelperText>
                            Список составлен из городов, указанных в анкетах пользователей
                        </FormHelperText>
                    </FormControl>
                )}

                {t.geo_mode === 'regions' && (
                    <FormControl sx={{ mb: 1.5 }}>
                        <FormLabel>Регионы</FormLabel>
                        <Autocomplete
                            multiple
                            options={dictionaries?.regions || []}
                            value={t.regions}
                            onChange={(_, v) => set({ regions: v })}
                            disabled={disabled}
                            placeholder="Начните вводить название"
                            limitTags={8}
                        />
                    </FormControl>
                )}

                <FormControl>
                    <FormLabel>Исключить города</FormLabel>
                    <Autocomplete
                        multiple
                        options={dictionaries?.cities || []}
                        value={t.exclude_cities}
                        onChange={(_, v) => set({ exclude_cities: v })}
                        disabled={disabled}
                        placeholder="Необязательно"
                        limitTags={6}
                    />
                </FormControl>
            </Sheet>

            {/* --- ДЕМОГРАФИЯ --- */}
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                <Typography level="title-sm" sx={{ mb: 1.5 }}>Аудитория</Typography>

                <Stack spacing={1.8}>
                    <MultiChips
                        label="Пол"
                        options={genderOptions}
                        value={t.genders}
                        onChange={(genders) => set({ genders })}
                        disabled={disabled}
                        helper="Ничего не выбрано — показываем всем"
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Возраст от</FormLabel>
                            <Input
                                type="number"
                                value={t.age_min ?? ''}
                                onChange={(e) => set({
                                    age_min: e.target.value ? Number(e.target.value) : null,
                                })}
                                disabled={disabled}
                                placeholder="18"
                            />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Возраст до</FormLabel>
                            <Input
                                type="number"
                                value={t.age_max ?? ''}
                                onChange={(e) => set({
                                    age_max: e.target.value ? Number(e.target.value) : null,
                                })}
                                disabled={disabled}
                                placeholder="без ограничения"
                            />
                        </FormControl>
                    </Stack>

                    <MultiChips
                        label="Доход"
                        options={incomeOptions}
                        value={t.incomes}
                        onChange={(incomes) => set({ incomes })}
                        disabled={disabled}
                        helper="Ничего не выбрано — любой доход"
                    />

                    <MultiChips
                        label="Цель знакомства"
                        options={purposeOptions}
                        value={t.purposes}
                        onChange={(purposes) => set({ purposes })}
                        disabled={disabled}
                        helper="Ничего не выбрано — любая цель"
                    />

                    <MultiChips
                        label="Платформа"
                        options={platformOptions}
                        value={t.platforms}
                        onChange={(platforms) => set({ platforms })}
                        disabled={disabled}
                        helper="Ничего не выбрано — все платформы"
                    />
                </Stack>
            </Sheet>

            {/* --- ИНТЕРЕСЫ --- */}
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                <Typography level="title-sm" sx={{ mb: 1.5 }}>Интересы и занятость</Typography>

                <Stack spacing={1.5}>
                    <FormControl>
                        <FormLabel>Хобби</FormLabel>
                        <Autocomplete
                            multiple
                            options={hobbies}
                            getOptionLabel={(o) => o?.name || ''}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            value={hobbies.filter((h) => t.hobby_ids.includes(h.id))}
                            onChange={(_, v) => set({ hobby_ids: v.map((h) => h.id) })}
                            disabled={disabled}
                            placeholder="Любые"
                            limitTags={8}
                        />
                        <FormHelperText>
                            Достаточно совпадения по одному хобби из списка
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel>Сфера деятельности</FormLabel>
                        <Autocomplete
                            multiple
                            options={fields}
                            getOptionLabel={(o) => o?.name || ''}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            value={fields.filter((f) => t.field_ids.includes(f.id))}
                            onChange={(_, v) => set({ field_ids: v.map((f) => f.id) })}
                            disabled={disabled}
                            placeholder="Любая"
                            limitTags={8}
                        />
                    </FormControl>
                </Stack>
            </Sheet>

            {/* --- ПОВЕДЕНИЕ --- */}
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                <Typography level="title-sm" sx={{ mb: 1.5 }}>Дополнительно</Typography>

                <Stack spacing={1.5}>
                    <FormControl>
                        <FormLabel>Статус подписки</FormLabel>
                        <Select
                            value={t.only_paid === null ? 'any' : (t.only_paid ? 'paid' : 'free')}
                            onChange={(_, v) => set({
                                only_paid: v === 'any' ? null : v === 'paid',
                            })}
                            disabled={disabled}
                        >
                            <Option value="any">Все пользователи</Option>
                            <Option value="paid">Только с активным тарифом</Option>
                            <Option value="free">Только без тарифа</Option>
                        </Select>
                    </FormControl>

                    <Checkbox
                        label="Только пользователи с фотографией в анкете"
                        checked={t.has_photo === true}
                        onChange={(e) => set({ has_photo: e.target.checked ? true : null })}
                        disabled={disabled}
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Возраст аккаунта от, дней</FormLabel>
                            <Input
                                type="number"
                                value={t.account_age_days_min ?? ''}
                                onChange={(e) => set({
                                    account_age_days_min: e.target.value ? Number(e.target.value) : null,
                                })}
                                disabled={disabled}
                                placeholder="любой"
                            />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>до, дней</FormLabel>
                            <Input
                                type="number"
                                value={t.account_age_days_max ?? ''}
                                onChange={(e) => set({
                                    account_age_days_max: e.target.value ? Number(e.target.value) : null,
                                })}
                                disabled={disabled}
                                placeholder="любой"
                            />
                        </FormControl>
                    </Stack>
                    <FormHelperText sx={{ mt: -1 }}>
                        Новички активнее, но пока не привыкли к сервису
                    </FormHelperText>

                    {!!(dictionaries?.traits || []).length && (
                        <>
                            <Divider />
                            <TraitsEditor
                                traits={dictionaries.traits}
                                value={t.traits}
                                onChange={(traits) => set({ traits })}
                                disabled={disabled}
                            />
                        </>
                    )}
                </Stack>
            </Sheet>
        </Stack>
    );
}


/**
 * Таргетинг по параметрам из тестов. Каждое правило — код параметра
 * и диапазон значений. Пользователи без пройденного теста в такой
 * таргетинг не попадают — об этом стоит предупредить явно.
 */
function TraitsEditor({ traits, value, onChange, disabled }) {
    const rules = value || [];

    const addRule = () => onChange([...rules, { code: traits[0]?.code, min: 0, max: 100 }]);
    const updateRule = (index, patch) => onChange(
        rules.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
    const removeRule = (index) => onChange(rules.filter((_, i) => i !== index));

    return (
        <Box>
            <FormLabel sx={{ mb: 1 }}>Параметры из тестов</FormLabel>

            {rules.length === 0 && (
                <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
                    Не используются — показываем всем
                </Typography>
            )}

            <Stack spacing={1}>
                {rules.map((rule, index) => (
                    <Stack key={index} direction="row" spacing={1} alignItems="flex-end">
                        <FormControl sx={{ flex: 2 }}>
                            <Select
                                size="sm"
                                value={rule.code}
                                onChange={(_, v) => v && updateRule(index, { code: v })}
                                disabled={disabled}
                            >
                                {traits.map((trait) => (
                                    <Option key={trait.code} value={trait.code}>{trait.name}</Option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <Input
                                size="sm"
                                type="number"
                                value={rule.min ?? ''}
                                onChange={(e) => updateRule(index, {
                                    min: e.target.value ? Number(e.target.value) : null,
                                })}
                                placeholder="от"
                                disabled={disabled}
                            />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <Input
                                size="sm"
                                type="number"
                                value={rule.max ?? ''}
                                onChange={(e) => updateRule(index, {
                                    max: e.target.value ? Number(e.target.value) : null,
                                })}
                                placeholder="до"
                                disabled={disabled}
                            />
                        </FormControl>
                        <Chip
                            variant="soft"
                            color="danger"
                            disabled={disabled}
                            onClick={() => removeRule(index)}
                            sx={{ cursor: 'pointer' }}
                        >
                            Убрать
                        </Chip>
                    </Stack>
                ))}
            </Stack>

            <Chip
                variant="outlined"
                color="primary"
                disabled={disabled}
                onClick={addRule}
                sx={{ mt: 1, cursor: 'pointer' }}
            >
                + Добавить параметр
            </Chip>

            {rules.length > 0 && (
                <Alert color="warning" variant="soft" size="sm" sx={{ mt: 1 }}>
                    Пользователи, не прошедшие соответствующий тест, в таргетинг не попадут.
                    Это сильно сужает охват.
                </Alert>
            )}
        </Box>
    );
}